// The four quick-pick amounts under OfferComposer's field, per mode.
//
// Here rather than inside the SFC for the reason bidSheetArithmetic.js is: a
// pure function a test can call directly is pinned at its boundaries, while the
// same arithmetic in a `computed` is only reachable by mounting the composer in
// each mode and reading chip text back out of the DOM.
//
// The math is the handoff's, verbatim (COMPONENTS.md, OfferComposer):
//
//   make     Suggested (92% of high bid, rounded to $50) · High bid ·
//            Split (mid of high bid and ask) · Ask
//   counter  Split (mid of yours and theirs) · Last +$200 · Meet −$200
//            (theirs − 200) · Hold (yours)
//   revise   Current · +$200 · +$500 · Ask
//
// A chip whose inputs are missing is LEFT OUT rather than drawn with no amount:
// a chip is a button that sets the field, and one with nothing to set is a
// button that does nothing. So an asset with no high bid offers only Ask in make
// mode. The spec is silent on this; the composer's chips share the row by
// `flex: 1`, so fewer chips still fill it.
//
// Deliberately NOT exported from the package barrel: the catalog gate holds that
// a barrel exports components and nothing else.

/** The three modes the composer runs in, and the one set its `mode` validator reads. */
export const OFFER_COMPOSER_MODES = Object.freeze(['make', 'counter', 'revise']);

const isAmount = (value) => typeof value === 'number' && Number.isFinite(value);

/**
 * 92% of the high bid, to the nearest $50, halves rounding up.
 *
 * Computed in whole numbers — `highBid × 92 / 5000` is the count of $50 steps —
 * so a figure that lands exactly on a half ($625 → $575 → $600) rounds the same
 * way every time instead of at the mercy of `0.92` in binary floating point.
 */
export const suggestedAmount = (highBid) => Math.round((highBid * 92) / 5000) * 50;

/**
 * The midpoint of two amounts, to the whole dollar, halves rounding up.
 *
 * Whole dollars because every amount the composer holds is one; the spec rounds
 * only Suggested to $50, so Split is not.
 */
export const splitAmount = (a, b) => Math.round((a + b) / 2);

const chip = (id, label, amount) => ({ id, label, amount });

const CHIP_SETS = {
  make: ({ ask, highBid }) => [
    isAmount(highBid) && chip('suggested', 'Suggested', suggestedAmount(highBid)),
    isAmount(highBid) && chip('highBid', 'High bid', highBid),
    isAmount(highBid) && isAmount(ask) && chip('split', 'Split', splitAmount(highBid, ask)),
    isAmount(ask) && chip('ask', 'Ask', ask),
  ],
  counter: ({ yours, theirs }) => [
    isAmount(yours) && isAmount(theirs) && chip('split', 'Split', splitAmount(yours, theirs)),
    isAmount(yours) && chip('last', 'Last +$200', yours + 200),
    isAmount(theirs) && chip('meet', 'Meet −$200', theirs - 200),
    isAmount(yours) && chip('hold', 'Hold', yours),
  ],
  revise: ({ ask, yours }) => [
    isAmount(yours) && chip('current', 'Current', yours),
    isAmount(yours) && chip('plus200', '+$200', yours + 200),
    isAmount(yours) && chip('plus500', '+$500', yours + 500),
    isAmount(ask) && chip('ask', 'Ask', ask),
  ],
};

/**
 * The quick-pick chips for one mode, in the order the row draws them, as
 * `{ id, label, amount }`. An unknown mode has no chips.
 */
export function offerChips({ mode, ask = null, highBid = null, yours = null, theirs = null }) {
  const build = CHIP_SETS[mode];
  return build ? build({ ask, highBid, yours, theirs }).filter(Boolean) : [];
}
